import React, { useState, useReducer, useEffect, Fragment } from 'react';
import { CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import QRCode from 'qrcode';
import cn from 'classnames';
import Head from 'next/head';
import SnackInfo from '@ictlife/core/components/SnackInfo';
import Cookies from '@ictlife/core/config/cookies';
import Image from 'next/image';
import { useRouter } from 'next/router';
import loginReducer, {
  initialLoginState,
} from '../../state/reducers/loginReducer';
import {
  getQrJwtToken,
  refreshQrToken,
  initiateQrLogin,
} from '../../state/actions/qrCodeLoginAction';

import styles from './styles/Login.module.scss';
import UsernameOption from './UsernameOption';
import {
  redirectLoggedInUser,
  storeAndRedirectUser,
} from './storeAndRedirectUser';

const Login = ({ appName, appInstructions, AppLink }) => {
  const [timeoutState, setTimeoutState] = useState({
    timeoutOn: false,
    timeoutValue: 0,
  });
  const [snackInfo, setSnackInfo] = useState({
    open: false,
    message: '',
    vertical: 'bottom',
    horizontal: 'right',
    severity: 'info',
    autoHideDuration: 5000,
  });
  const dispatch = useDispatch();
  const router = useRouter();

  const [loginState, dispatchLoginUser] = useReducer(
    loginReducer,
    initialLoginState
  );

  const qrCodeLoginState = useSelector(store => store.qrCodeLoginState);

  useEffect(() => {
    getQrJwtToken(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (Cookies.get('ictl_token')) {
      const redirect = router.query.redirect;
      redirectLoggedInUser({ router, redirect, dispatch });
    }
  }, [dispatch, router]);

  useEffect(() => {
    return () => clearTimeout(timeoutState.timeoutValue);
  }, [timeoutState.timeoutValue]);

  if (qrCodeLoginState.isSuccess) {
    QRCode.toCanvas(
      document.querySelector('#qrcode'),
      qrCodeLoginState.token && qrCodeLoginState.token,
      { errorCorrectionLevel: 'M', margin: 2 }
    )
      .then(() => {
        if (!timeoutState.timeoutOn) {
          const timeoutValue = setTimeout(() => {
            const body = {
              token: qrCodeLoginState.token,
            };
            setTimeoutState(timeout => ({
              ...timeout,
              timeoutOn: false,
            }));
            refreshQrToken(dispatch, body);
          }, 30000);
          setTimeoutState({
            timeoutOn: true,
            timeoutValue,
          });
        }
      })
      .catch(err => {
        console.error(err);
      });
  }

  if (qrCodeLoginState.isError) {
    clearTimeout(timeoutState.timeoutValue);
  }

  if (qrCodeLoginState.isInitial) {
    dispatch({ type: 'RESET_QR_CODE_STATE' });
    const url = `${process.env.NEXT_PUBLIC_WEB_SOCKET_URL}?token=${qrCodeLoginState.token}`;
    let ws = new WebSocket(url);
    ws.onmessage = evt => {
      const data = JSON.parse(evt.data);
      if (data.message_type === 'scan_success') {
        const payload = {
          token: data.token,
        };
        dispatchLoginUser({ type: 'LOADING' });
        initiateQrLogin(payload)
          .then(response => {
            const redirect = router.query.redirect;
            storeAndRedirectUser({
              authResponse: response,
              router,
              redirect,
              dispatch,
            });
          })
          .catch(error => {
            try {
              dispatchLoginUser({
                type: 'ERROR',
                errorMessage: error.response.data.error_message,
              });
            } catch (error) {
              dispatchLoginUser({
                type: 'ERROR',
                errorMessage:
                  'QR code login failed. Please try again later or try other login options',
              });
            }
          });
      }
    };
  }

  const openQRCodeOption = () => {
    document.querySelector('#slider-wrapper').style.transform =
      'translateX(0%)';
    document
      .querySelector('#username-password-option')
      .classList.add('!hidden');
    document.querySelector('#qrcode-option').classList.remove('!hidden');
  };

  const openPasswordOption = () => {
    document.querySelector('#slider-wrapper').style.transform =
      'translateX(-80%)';
    document
      .querySelector('#username-password-option')
      .classList.remove('!hidden');
    document.querySelector('#qrcode-option').classList.add('!hidden');
  };

  return (
    <Fragment>
      <Head>
        <title>Login | ICTLIFE {appName}</title>
      </Head>
      <div className={styles.loginWrapper}>
        <div className={styles.mobileWrapper}>
          <UsernameOption
            loginState={loginState}
            dispatchLoginUser={dispatchLoginUser}
          />
        </div>
        <div className={styles.loginOptionsWrapper}>
          <div className={styles.sliderWrapper} id="slider-wrapper">
            <div id="username-password-option">
              <UsernameOption
                loginState={loginState}
                dispatchLoginUser={dispatchLoginUser}
              />
            </div>

            <div
              className={cn(styles.qrcodeOption, '!hidden')}
              id="qrcode-option"
            >
              <div className={styles.wrapper}>
                <h1 className="theme-text">SCAN QR CODE</h1>
                <div className={styles.qrHolder}>
                  {(qrCodeLoginState.isError || qrCodeLoginState.isLoading) && (
                    <div className={styles.overlay}></div>
                  )}
                  {qrCodeLoginState.isLoading && (
                    <div className={styles.progressContainer}>
                      <CircularProgress className={styles.progress} />
                    </div>
                  )}
                  {qrCodeLoginState.isError && (
                    <div
                      className={styles.redoContainer}
                      onClick={() => {
                        clearTimeout(timeoutState.timeoutValue);
                        getQrJwtToken(dispatch);
                        setTimeoutState(() => ({
                          timeoutOn: false,
                          timeoutValue: 0,
                        }));
                      }}
                    >
                      <span className="material-icons">&#xe5d5;</span>
                    </div>
                  )}
                  {!qrCodeLoginState.isLoading && !qrCodeLoginState.isError && (
                    <div className={styles.companyLogo}>
                      <Image
                        src="/images/logo/sasa-logo-top.svg"
                        alt="Sasa"
                        layout="fill"
                      />
                    </div>
                  )}
                  <canvas id="qrcode" className={styles.qrCodeCanvas}></canvas>
                </div>
                <div className={styles.getApp}>
                  Don&apos;t have the app yet? Get the <AppLink />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.leftBox}>
            {appInstructions(styles)}

            <div className={styles.footer}>
              <p>Use username &amp; password instead ?</p>
              <button
                className="custom-button outlined lc"
                onClick={openPasswordOption}
              >
                Use Password
              </button>
            </div>
          </div>
          <div className={styles.rightBox}>
            <div className={styles.wrapper}>
              <div className={styles.cover}>
                <div className={cn(styles.comp, 'theme-text')}>Africa Sasa</div>
                {/* <div className={styles.app}>{appName}</div> */}
                <div className={styles.logoDiv}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/images/logo/sasa-logo-top.svg"
                    height="auto"
                    width="100%"
                    alt="Sasa Logo"
                  />
                </div>
              </div>
              <div className={styles.action}>
                <p>Already have the app?</p>
                <button
                  className="custom-button outlined lc"
                  onClick={() => {
                    openQRCodeOption();
                  }}
                >
                  Use QR Code
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SnackInfo
        snackInfo={snackInfo}
        handleClose={() =>
          setSnackInfo(snackInfo => ({
            ...snackInfo,
            message: '',
            open: false,
          }))
        }
      />
    </Fragment>
  );
};

export default Login;
